import { useCallback, useRef } from 'react';

// Detect iOS devices
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const useTouchHandler = (onTap, onLongPress, options = {}) => {
  const {
    longPressDelay = 500
  } = typeof options === 'number' ? { longPressDelay: options } : options;

  const touchStateRef = useRef({
    isPressed: false,
    startTime: 0,
    isLongPress: false,
    touchSequenceId: null
  });

  const timeoutRef = useRef(null);
  const touchStartTimeRef = useRef(0);
  const hasFiredLongPressRef = useRef(false);
  const touchSequenceIdRef = useRef(0);
  const hasMoved = useRef(false);
  const startPositionRef = useRef({ x: 0, y: 0 });
  const longPressBlockUntilRef = useRef(0); // Block taps until this timestamp

  // iOS-specific settings
  const iosDevice = isIOS();
  const effectiveLongPressDelay = iosDevice ? longPressDelay + 100 : longPressDelay; // Slightly longer for iOS
  const moveThreshold = 10; // pixels

  const handleTouchStart = useCallback((e) => {
    // Clear any existing timeout first
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    e.preventDefault();
    const currentTime = Date.now();
    touchStartTimeRef.current = currentTime;
    hasFiredLongPressRef.current = false;
    hasMoved.current = false;
    touchSequenceIdRef.current++;

    // Store initial touch position
    const touch = e.touches ? e.touches[0] : e;
    startPositionRef.current = { x: touch.clientX, y: touch.clientY };

    touchStateRef.current = {
      isPressed: true,
      startTime: currentTime,
      isLongPress: false,
      touchSequenceId: touchSequenceIdRef.current
    };

    timeoutRef.current = setTimeout(() => {
      // Double check that we haven't moved and haven't already fired
      if (!hasFiredLongPressRef.current && !hasMoved.current) {
        hasFiredLongPressRef.current = true;
        // Block taps for 3 seconds after long press (iOS Safari multiple events fix)
        longPressBlockUntilRef.current = Date.now() + 3000;
        touchStateRef.current = {
          ...touchStateRef.current,
          isLongPress: true
        };
        if (onLongPress) {
          onLongPress(e);
        }
      }
    }, effectiveLongPressDelay);
  }, [onTap, onLongPress, effectiveLongPressDelay]);

  const handleTouchMove = useCallback((e) => {
    if (!touchStateRef.current.isPressed) return;

    const touch = e.touches ? e.touches[0] : e;
    const deltaX = Math.abs(touch.clientX - startPositionRef.current.x);
    const deltaY = Math.abs(touch.clientY - startPositionRef.current.y);

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      hasMoved.current = true;

      // Cancel long press if user has moved too much
      if (timeoutRef.current && !hasFiredLongPressRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [moveThreshold]);

  const handleTouchEnd = useCallback((e) => {
    const touchDuration = Date.now() - touchStartTimeRef.current;

    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset state immediately
    touchStateRef.current = {
      isPressed: false,
      startTime: 0,
      isLongPress: false,
      touchSequenceId: null
    };

    const now = Date.now();

    // Primary protection: timestamp-based blocking after long press
    if (now < longPressBlockUntilRef.current) {
      return;
    }

    // Secondary protection: long press state check
    if (hasFiredLongPressRef.current) {
      return;
    }

    // Fire tap on release if conditions are met
    if (!hasFiredLongPressRef.current &&
        touchDuration < effectiveLongPressDelay &&
        !hasMoved.current) {
      if (onTap) {
        onTap(e);
      }
    }

    // Reset state after delay (timestamp blocker expires naturally)
    setTimeout(() => {
      hasFiredLongPressRef.current = false;
      hasMoved.current = false;
    }, 2000);
  }, [onTap, effectiveLongPressDelay]);

  const handleTouchCancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    touchStateRef.current = {
      isPressed: false,
      startTime: 0,
      isLongPress: false,
      touchSequenceId: null
    };

    // Reset state after delay
    setTimeout(() => {
      hasFiredLongPressRef.current = false;
      hasMoved.current = false;
    }, 500);
  }, []);

  const touchHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchCancel,
    onMouseDown: handleTouchStart,
    onMouseMove: handleTouchMove,
    onMouseUp: handleTouchEnd,
    onMouseLeave: handleTouchCancel
  };

  return { touchState: touchStateRef.current, touchHandlers };
};

export default useTouchHandler;
